import { IImageFormat, DecodedImage, LayerData } from "../format-interface";
import { ShapeStyle } from "@hcie/shared";

/**
 * @class SvgFormat
 * @description Decodes SVG files into vector layers with path data.
 */
export class SvgFormat implements IImageFormat {
    readonly name = "Scalable Vector Graphics";
    readonly extensions = [".svg"];
    readonly canRead = true;
    readonly canWrite = false; // Only import for now
    readonly priority = 15;

    canHandle(buffer: ArrayBuffer): boolean {
        const header = new TextDecoder().decode(buffer.slice(0, 50));
        return header.includes("<svg") || header.includes("<?xml");
    }

    async read(buffer: ArrayBuffer): Promise<DecodedImage> {
        const text = new TextDecoder().decode(buffer);
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, "image/svg+xml");
        const svg = doc.querySelector("svg");

        if (!svg) throw new Error("Invalid SVG file");

        const width = parseInt(svg.getAttribute("width") || "800");
        const height = parseInt(svg.getAttribute("height") || "600");

        const layers: LayerData[] = [];
        const shapes: any[] = [];

        // Simple DFS for paths, rects, circles
        this.extractShapes(svg, shapes);

        layers.push({
            name: "SVG Vector Layer",
            canvas: new ImageData(1, 1), // Raster placeholder
            visible: true,
            opacity: 1.0,
            blendMode: "source-over",
            x: 0,
            y: 0,
            type: 'vector',
            shapes: shapes
        });

        return { width, height, layers };
    }

    private extractShapes(element: Element, shapes: any[]) {
        const children = element.children;
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            const tag = child.tagName.toLowerCase();

            if (tag === 'path') {
                const d = child.getAttribute('d');
                if (d) {
                    shapes.push({
                        id: 'svg-' + Math.random().toString(36).substr(2, 9),
                        type: 'path',
                        pathData: d,
                        style: this.getStyle(child)
                    });
                }
            } else if (tag === 'rect') {
                shapes.push({
                    id: 'svg-' + Math.random().toString(36).substr(2, 9),
                    type: 'rect',
                    x1: parseFloat(child.getAttribute('x') || '0'),
                    y1: parseFloat(child.getAttribute('y') || '0'),
                    x2: parseFloat(child.getAttribute('x') || '0') + parseFloat(child.getAttribute('width') || '0'),
                    y2: parseFloat(child.getAttribute('y') || '0') + parseFloat(child.getAttribute('height') || '0'),
                    style: this.getStyle(child)
                });
            } else if (tag === 'circle') {
                const cx = parseFloat(child.getAttribute('cx') || '0');
                const cy = parseFloat(child.getAttribute('cy') || '0');
                const r = parseFloat(child.getAttribute('r') || '0');
                shapes.push({
                    id: 'svg-' + Math.random().toString(36).substr(2, 9),
                    type: 'circle',
                    x1: cx,
                    y1: cy,
                    x2: cx + r, // Our circle uses radial distance for x2
                    y2: cy,
                    style: this.getStyle(child)
                });
            } else if (tag === 'g') {
                this.extractShapes(child, shapes);
            }
        }
    }

    private getStyle(el: Element): ShapeStyle {
        const fill = el.getAttribute('fill') || '#000000';
        const stroke = el.getAttribute('stroke') || 'none';
        const strokeWidth = parseFloat(el.getAttribute('stroke-width') || '1');
        
        return {
            color: stroke === 'none' ? fill : stroke,
            fillColor: fill === 'none' ? undefined : fill,
            fill: fill !== 'none',
            width: stroke === 'none' ? 0 : strokeWidth,
            opacity: parseFloat(el.getAttribute('opacity') || '1.0'),
            cap: 'round',
            hardness: 1.0
        };
    }

    async write(): Promise<ArrayBuffer> {
        throw new Error("SVG Export not implemented yet.");
    }
}
